import React, { useState } from 'react';
import { ROOMS, WHATSAPP_LINK, DORM_IMAGE } from '../data/mockData';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Users, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';

const RoomCard = ({ room, index }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  const nextImage = (e) => { e.stopPropagation(); setCurrentImage((prev) => (prev + 1) % room.images.length); };
  const prevImage = (e) => { e.stopPropagation(); setCurrentImage((prev) => (prev - 1 + room.images.length) % room.images.length); };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.12 }}
      className="group relative bg-[#1E1E1E] rounded-2xl overflow-hidden border border-[#2A2A2A] hover:border-[#D4A843]/30 transition-all duration-500"
    >
      <div className="relative h-52 md:h-64 overflow-hidden">
        <img src={room.images[currentImage]} alt={room.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E1E1E] via-transparent to-transparent" />
        <div className="absolute top-4 left-4">
          <span className="bg-[#D4A843]/90 text-[#1C1C1C] text-[10px] font-semibold px-3 py-1.5 rounded-full uppercase tracking-wider">{room.category}</span>
        </div>
        {room.images.length > 1 && (
          <>
            <button onClick={prevImage} className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-black/60"><ChevronLeft size={16} /></button>
            <button onClick={nextImage} className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-black/60"><ChevronRight size={16} /></button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {room.images.map((_, i) => (<div key={`dot-${i}`} className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${i === currentImage ? 'bg-[#D4A843] w-4' : 'bg-white/40'}`} />))}
            </div>
          </>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-[#F5F0E8] font-heading">
            {room.name}
            {room.subtitle && <span className="block text-xs text-[#D4A843] font-normal mt-0.5">{room.subtitle}</span>}
          </h3>
          <div className="flex items-center gap-1.5 text-[#B0A898]"><Users size={13} /><span className="text-xs">{room.capacity}</span></div>
        </div>
        <p className="text-[#8A8070] text-sm leading-relaxed mb-4">{room.description}</p>
        <div className="flex flex-wrap gap-2 mb-5">
          {room.amenities.map((a) => (<span key={a} className="text-[10px] uppercase tracking-wider text-[#B0A898] bg-[#2A2A2A] px-2.5 py-1 rounded-full">{a}</span>))}
        </div>
        <div className="border-t border-[#2A2A2A] pt-4">
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-[#D4A843] text-[#1C1C1C] w-full py-3 rounded-full text-sm font-semibold hover:bg-[#E8B940] transition-colors duration-300 group/btn">
            Book Now <ArrowRight size={14} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
          </a>
        </div>
      </div>
    </motion.div>
  );
};

const RoomsPage = () => {
  return (
    <Layout>
      <PageHeader
        title="Pick your"
        titleAccent="vibe."
        subtitle="Whether you're a solo wanderer, a couple, or a whole family — we've got a bed with your name on it."
        bgImage={DORM_IMAGE}
      />
      <section className="pb-24 px-6 lg:px-8 bg-[#111111]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {ROOMS.map((room, i) => (<RoomCard key={room.id} room={room} index={i} />))}
          </div>
          <div className="text-center mt-12">
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#3A3A3A] text-[#F5F0E8] px-8 py-3.5 rounded-full text-sm font-medium hover:border-[#D4A843] hover:text-[#D4A843] transition-colors duration-300">
              Book Your Room on WhatsApp <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default RoomsPage;
